/**
 * Symptom Analysis Logic
 * Non-diagnostic symptom checker that relates symptoms to lifestyle factors
 * IMPORTANT: Avoids medical diagnosis language, uses supportive tone
 */

// Common symptoms mapped to lifestyle factors
const SYMPTOM_PATTERNS = {
  fatigue: {
    lifestyle_causes: ['poor_sleep', 'dehydration', 'low_activity', 'stress'],
    simple_text: 'Tiredness and low energy',
    insights: [
      { cause: 'poor_sleep', text: 'Fatigue from poor sleep quality' },
      { cause: 'dehydration', text: 'Low energy possibly linked to not drinking enough water' },
      { cause: 'low_activity', text: 'Tiredness that can come from lack of movement' },
      { cause: 'stress', text: 'Exhaustion that may be stress-related' }
    ]
  },
  
  headache: {
    lifestyle_causes: ['dehydration', 'stress', 'poor_sleep', 'screen_time'],
    simple_text: 'Head discomfort or pain',
    insights: [
      { cause: 'dehydration', text: 'Head discomfort possibly from mild dehydration' },
      { cause: 'stress', text: 'Tension that may be stress-related' },
      { cause: 'poor_sleep', text: 'Head discomfort that can follow poor sleep' },
      { cause: 'screen_time', text: 'Discomfort that can come from screen strain' }
    ]
  },
  
  dizziness: {
    lifestyle_causes: ['dehydration', 'low_food', 'poor_sleep'],
    simple_text: 'Feeling lightheaded or unsteady',
    insights: [
      { cause: 'dehydration', text: 'Lightheadedness possibly from not drinking enough' },
      { cause: 'low_food', text: 'Unsteadiness that can happen when you haven\'t eaten' },
      { cause: 'poor_sleep', text: 'Lightheadedness that may follow poor rest' }
    ],
    red_flag: true
  },
  
  stomach_upset: {
    lifestyle_causes: ['stress', 'poor_diet', 'dehydration'],
    simple_text: 'Digestive discomfort',
    insights: [
      { cause: 'stress', text: 'Digestive upset that can be stress-related' },
      { cause: 'poor_diet', text: 'Discomfort that may be linked to what you ate' },
      { cause: 'dehydration', text: 'Digestive issues that dehydration can contribute to' }
    ]
  },
  
  difficulty_sleeping: {
    lifestyle_causes: ['stress', 'screen_time', 'caffeine'],
    simple_text: 'Trouble falling or staying asleep',
    insights: [
      { cause: 'stress', text: 'Sleep difficulty that stress can cause' },
      { cause: 'screen_time', text: 'Trouble sleeping that can follow late screen use' },
      { cause: 'caffeine', text: 'Sleep disruption possibly from caffeine' }
    ]
  },
  
  anxiety: {
    lifestyle_causes: ['stress', 'poor_sleep', 'caffeine', 'low_activity'],
    simple_text: 'Feeling worried or on edge',
    insights: [
      { cause: 'stress', text: 'Worry that may be stress-related' },
      { cause: 'poor_sleep', text: 'Anxious feelings that poor sleep can worsen' },
      { cause: 'low_activity', text: 'Tension that movement can help relieve' }
    ]
  },
  
  brain_fog: {
    lifestyle_causes: ['poor_sleep', 'dehydration', 'stress'],
    simple_text: 'Difficulty concentrating or thinking clearly',
    insights: [
      { cause: 'poor_sleep', text: 'Mental fog that poor sleep can cause' },
      { cause: 'dehydration', text: 'Difficulty focusing that dehydration can contribute to' },
      { cause: 'stress', text: 'Mental cloudiness that stress can bring' }
    ]
  },
  
  muscle_aches: {
    lifestyle_causes: ['low_activity', 'stress', 'poor_sleep', 'dehydration'],
    simple_text: 'Body soreness or stiffness',
    insights: [
      { cause: 'low_activity', text: 'Stiffness from not moving enough' },
      { cause: 'stress', text: 'Muscle tension that stress can cause' },
      { cause: 'poor_sleep', text: 'Body aches that poor rest can worsen' }
    ]
  }
};

// Lifestyle actions mapped to causes
const LIFESTYLE_ACTIONS = {
  poor_sleep: [
    { text: 'Try going to bed 30 minutes earlier tonight', icon: '🌙' },
    { text: 'Create a calming bedtime routine', icon: '✨' }
  ],
  dehydration: [
    { text: 'Drink 2 glasses of water right now', icon: '💧' },
    { text: 'Keep water nearby throughout the day', icon: '💧' }
  ],
  stress: [
    { text: 'Take 5 deep breaths slowly', icon: '🧘' },
    { text: 'Step outside for fresh air', icon: '🌿' }
  ],
  low_activity: [
    { text: 'Take a 10-minute walk', icon: '🚶' },
    { text: 'Do some gentle stretches', icon: '🤸' }
  ],
  low_food: [
    { text: 'Eat something nourishing', icon: '🍎' },
    { text: 'Have a balanced snack', icon: '🥗' }
  ],
  screen_time: [
    { text: 'Take a break from screens for 20 minutes', icon: '📱' },
    { text: 'Try the 20-20-20 rule (every 20 min, look 20 feet away, 20 seconds)', icon: '👀' }
  ],
  poor_diet: [
    { text: 'Eat regular, balanced meals', icon: '🥗' },
    { text: 'Avoid skipping meals', icon: '⏰' }
  ],
  caffeine: [
    { text: 'Reduce caffeine after 2pm', icon: '☕' },
    { text: 'Try herbal tea instead', icon: '🍵' }
  ]
};

/**
 * Analyze symptoms with lifestyle context
 */
export const analyzeSymptoms = (symptoms, lifestyleData) => {
  const insights = [];
  const actions = new Set();
  const causes = new Set();
  let hasRedFlag = false;

  // Analyze each symptom
  symptoms.forEach(symptom => {
    const pattern = SYMPTOM_PATTERNS[symptom];
    if (!pattern) return;

    if (pattern.red_flag) hasRedFlag = true;

    // Match lifestyle causes
    pattern.lifestyle_causes.forEach(cause => {
      // Check if user's lifestyle matches this cause
      if (matchesLifestyle(cause, lifestyleData)) {
        // Find matching insight
        const insight = pattern.insights.find(i => i.cause === cause);
        if (insight) {
          insights.push({
            text: insight.text,
            cause: cause,
            symptom: pattern.simple_text
          });
          causes.add(cause);
        }
      }
    });
  });

  // Generate actions based on identified causes
  causes.forEach(cause => {
    const causeActions = LIFESTYLE_ACTIONS[cause];
    if (causeActions) {
      causeActions.forEach(action => actions.add(JSON.stringify(action)));
    }
  });

  // Convert actions back to objects and limit to top 3
  const actionList = Array.from(actions)
    .map(a => JSON.parse(a))
    .slice(0, 3);

  return {
    insights: insights.slice(0, 3), // Top 3 insights
    actions: actionList,
    hasRedFlag,
    severity: determineSeverity(symptoms, lifestyleData)
  };
};

/**
 * Check if user's lifestyle matches a cause
 */
const matchesLifestyle = (cause, lifestyleData) => {
  switch (cause) {
    case 'poor_sleep':
      return !lifestyleData.slept_well;
    case 'dehydration':
      return !lifestyleData.drank_water;
    case 'low_activity':
      return !lifestyleData.moved_body;
    case 'stress':
      return lifestyleData.stress_level === 'high';
    case 'low_food':
      return !lifestyleData.ate_well;
    default:
      return true; // Default match for general causes
  }
};

/**
 * Determine severity level for "when to seek help" guidance
 */
const determineSeverity = (symptoms, lifestyleData) => {
  // Check for red flag symptoms
  const redFlagSymptoms = ['dizziness', 'chest_pain', 'severe_headache'];
  const hasRedFlag = symptoms.some(s => redFlagSymptoms.includes(s));

  if (hasRedFlag) return 'urgent';
  if (symptoms.length >= 4) return 'moderate';
  return 'mild';
};

/**
 * Get "when to seek help" guidance based on severity
 */
export const getSeekHelpGuidance = (severity, hasRedFlag) => {
  if (severity === 'urgent' || hasRedFlag) {
    return {
      title: 'Consider seeking help soon',
      guidance: [
        'If symptoms are sudden or severe, contact NHS 111 or your GP today',
        'If you feel very unwell or symptoms worsen quickly, call 999'
      ],
      urgency: 'high'
    };
  }

  if (severity === 'moderate') {
    return {
      title: 'Monitor and consider professional advice',
      guidance: [
        'If symptoms continue for 3+ days without improvement, contact your GP',
        'If symptoms significantly affect your daily life, seek advice sooner',
        'Trust your instincts — if something feels wrong, it\'s okay to ask for help'
      ],
      urgency: 'medium'
    };
  }

  return {
    title: 'Keep an eye on how you feel',
    guidance: [
      'If symptoms continue for a week or get worse, consider contacting your GP',
      'Most lifestyle-related symptoms improve with rest, hydration, and self-care',
      'You know your body best — seek help if you\'re concerned'
    ],
    urgency: 'low'
  };
};

/**
 * Generate supportive summary message
 */
export const generateSummary = (insights) => {
  if (insights.length === 0) {
    return 'Your body is giving you some signals. Let\'s look at what might help.';
  }

  return 'Your body may be showing signs of:';
};
